import { logger } from '@tuic/logger';

/**
 * Fetches the latest blog post from the Mein Schiff blog.
 */
export const getLatestBlogPost = async (): Promise<WordpressPost | null> => {
  // Retrieve the latest blog post
  const url =
    'https://www.meinschiff.com/blog/wp-json/wp/v2/posts?page=1&per_page=1';

  try {
    const response = await fetch(url);
    const posts = await response.json();

    if (Array.isArray(posts) && posts.length === 1) {
      return posts[0];
    }
  } catch (e) {
    logger.error('[getLatestBlogPost]: Failed fetching the latest blog post.');
    logger.error(e);
  }

  return null;
};

/**
 * Fetches a media object from the Mein Schiff blog.
 */
export const getImageFromMediaId = async (
  mediaId: number,
): Promise<WordpressMedia | null> => {
  const mediaUrl = `https://www.meinschiff.com/blog/wp-json/wp/v2/media/${mediaId}`;
  try {
    const mediaResponse = await fetch(mediaUrl);
    return mediaResponse.json();
  } catch (e) {
    logger.error('[getImageFromMediaId]: Failed fetching image from media ID.');
    logger.error(e);
  }

  return null;
};

export type WordpressPost = {
  id: number;
  date: string;
  slug: string;
  status: string;
  type: string;
  link: string;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
    protected: boolean;
  };
  excerpt: {
    rendered: string;
    protected: boolean;
  };
  author: number;
  featured_media: number;
  comment_status: string;
  ping_status: string;
  sticky: boolean;
  template: string;
  format: string;
  meta: any[];
  categories: number[];
  tags: any[];
  _links: {
    self: { href: string }[];
    collection: { href: string }[];
    about: { href: string }[];
    author: { href: string }[];
    replies: { href: string }[];
    'version-history': { href: string }[];
    'predecessor-version': { href: string }[];
    'wp:featuredmedia': { href: string }[];
    'wp:attachment': { href: string }[];
    'wp:term': { href: string }[];
    curies: { name: string; href: string; templated: boolean }[];
  };
};

export type WordpressMedia = {
  id: number;
  date: string;
  date_gmt: string;
  guid: {
    rendered: string;
  };
  modified: string;
  modified_gmt: string;
  slug: string;
  status: string;
  type: string;
  link: string;
  title: {
    rendered: string;
  };
  author: number;
  comment_status: string;
  ping_status: string;
  template: string;
  meta: Array<any>;
  yoast_head: string;
  yoast_head_json: {
    title: string;
    robots: {
      index: string;
      follow: string;
      'max-snippet': string;
      'max-image-preview': string;
      'max-video-preview': string;
    };
    canonical: string;
    og_locale: string;
    og_type: string;
    og_title: string;
    og_url: string;
    og_site_name: string;
    article_modified_time: string;
    og_image: Array<{
      width: number;
      height: number;
      url: string;
      type: string;
    }>;
    twitter_card: string;
    schema: {
      '@context': string;
      '@graph': Array<{
        '@type': string;
        '@id': string;
        url?: string;
        name?: string;
        isPartOf?: {
          '@id': string;
        };
        datePublished?: string;
        dateModified?: string;
        breadcrumb?: {
          '@id': string;
        };
        inLanguage?: string;
        potentialAction?: Array<{
          '@type': string;
          target: any;
          'query-input'?: string;
        }>;
        itemListElement?: Array<{
          '@type': string;
          position: number;
          name: string;
          item?: string;
        }>;
        description?: string;
      }>;
    };
  };
  description: {
    rendered: string;
  };
  caption: {
    rendered: string;
  };
  alt_text: string;
  media_type: string;
  mime_type: string;
  media_details: {
    width: number;
    height: number;
    file: string;
    filesize: number;
    sizes: {};
    image_meta: {
      aperture: string;
      credit: string;
      camera: string;
      caption: string;
      created_timestamp: string;
      copyright: string;
      focal_length: string;
      iso: string;
      shutter_speed: string;
      title: string;
      orientation: string;
      keywords: Array<any>;
    };
  };
  post: number;
  source_url: string;
  _links: {
    self: Array<{
      href: string;
    }>;
    collection: Array<{
      href: string;
    }>;
    about: Array<{
      href: string;
    }>;
    author: Array<{
      embeddable: boolean;
      href: string;
    }>;
    replies: Array<{
      embeddable: boolean;
      href: string;
    }>;
  };
};
